/**
 * DemoTour — headless onboarding storyboard for demo visitors.
 *
 * Built on the design system's own primitives (Framer Motion + brand tokens +
 * Lucide + a portal), NOT a generic tour lib: zero AGPL/bundle/ARIA-conflict
 * risk, fully on-brand. It is generic over ``DEMO_TOUR_STEPS`` — each step
 * optionally navigates to a workspace tab, then spotlights a ``data-tour``
 * anchor with an on-brand popover card.
 *
 * a11y: ``role="dialog"`` + ``aria-modal`` + labelled/described by the step
 * copy, focus moves to the primary control on each step, Tab is trapped within
 * the card, Esc skips, ←/→ navigate, and ``prefers-reduced-motion`` is honored.
 *
 * Render once in the authed shell; it no-ops unless the store is ``active``.
 */
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";

import { cn, useReducedMotion } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, X } from "lucide-react";

import { getWorkspaceTabDef } from "@/features/workspace/config";
import { useWorkspaceStore } from "@/features/workspace-router";

import { CARD_WIDTH, type Rect, SPOT_PAD, placeCard, readRect } from "./demoTourGeometry";
import { DEMO_TOUR_STEPS } from "./demoTourSteps";
import { useDemoTourStore } from "./demoTourStore";
import { useOnboardingMode } from "./useOnboardingMode";
import { useOnboardingPersistence } from "./useOnboardingPersistence";

export function DemoTour(): React.ReactElement | null {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const setTab = useWorkspaceStore((s) => s.setTab);
  const reducedMotion = useReducedMotion();
  const mode = useOnboardingMode();

  const active = useDemoTourStore((s) => s.active);
  const stepIndex = useDemoTourStore((s) => s.stepIndex);
  const setStepIndex = useDemoTourStore((s) => s.setStepIndex);
  const finish = useDemoTourStore((s) => s.finish);
  const { markSeen } = useOnboardingPersistence();

  const total = DEMO_TOUR_STEPS.length;
  const step = active ? DEMO_TOUR_STEPS[Math.min(stepIndex, total - 1)] : undefined;

  const [rect, setRect] = useState<Rect | null>(null);
  const [cardH, setCardH] = useState(220);
  // Live card node tracked via a callback ref so effects re-bind on remount.
  const [cardEl, setCardEl] = useState<HTMLDialogElement | null>(null);

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === total - 1;

  // End the tour (finish or skip): clear runtime state AND persist "seen" for
  // the audience (DB for real users, store/localStorage for demo).
  const endTour = useCallback(() => {
    markSeen();
    finish();
  }, [markSeen, finish]);

  const goNext = useCallback(() => {
    if (isLast) endTour();
    else setStepIndex(Math.min(stepIndex + 1, total - 1));
  }, [isLast, endTour, setStepIndex, stepIndex, total]);

  const goPrev = useCallback(() => {
    if (!isFirst) setStepIndex(Math.max(stepIndex - 1, 0));
  }, [isFirst, setStepIndex, stepIndex]);

  // Navigate to the step's section so its real content sits behind the
  // spotlight. The tab bar (and thus the anchor) is always mounted.
  useEffect(() => {
    if (!step) return;
    if (step.tab) {
      // Tab-scoped steps (Conversazioni, Calendario, …) → open that tab's route.
      setTab(step.tab, "manual");
      const def = getWorkspaceTabDef(step.tab);
      if (def?.route) void navigate({ to: def.route });
      return;
    }
    // Tab-less steps — the composer/focus/workspace-tabs spotlights and the
    // centered welcome/finish cards — anchor to the workspace SHELL. That shell
    // is unmounted under modal routes (e.g. the tour launched from /settings via
    // "Review the guided tour"), so the spotlight target never resolves and the
    // card floats over settings. Return to the workspace when we're off it; when
    // already on /workspace we leave the current tab untouched (no reset).
    if (!pathname.startsWith("/workspace")) {
      void navigate({ to: "/workspace/conversations" });
    }
  }, [step, pathname, setTab, navigate]);

  // Resolve + keep the spotlight rect in sync (after nav settles, on resize /
  // scroll). Retries briefly because a freshly-navigated route may still be
  // laying out when the step first renders.
  useLayoutEffect(() => {
    if (!step) return;
    if (!step.target) {
      setRect(null);
      return;
    }
    let frame = 0;
    let tries = 0;
    const tick = (): void => {
      const next = readRect(step.target as string);
      if (next) {
        setRect(next);
        return;
      }
      if (tries++ < 20) frame = requestAnimationFrame(tick); // ~330ms budget
    };
    tick();

    const onMove = (): void => {
      const next = readRect(step.target as string);
      if (next) setRect(next);
    };
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [step]);

  // Measure the card (for placement) via ResizeObserver bound to the live node,
  // and move focus to the primary control. ``cardEl`` is state set by a callback
  // ref, so this re-binds + re-focuses when the card remounts per step — no
  // trigger dependency, no exhaustive-deps noise, no autoFocus attribute.
  useLayoutEffect(() => {
    if (!cardEl) return;
    setCardH(cardEl.offsetHeight);
    cardEl.querySelector<HTMLButtonElement>("[data-tour-primary]")?.focus();
    const ro = new ResizeObserver(() => setCardH(cardEl.offsetHeight));
    ro.observe(cardEl);
    return () => ro.disconnect();
  }, [cardEl]);

  // Global keys: Esc skips, ←/→ navigate. ``goNext``/``goPrev`` change per step
  // (they close over ``stepIndex``), so the listener re-binds each step.
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        endTour();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, endTour, goNext, goPrev]);

  if (!active || !step) return null;

  const pos = placeCard(rect, step.placement, cardH);
  const ease = [0.32, 0.72, 0, 1] as const;

  // Minimal focus trap: keep Tab cycling within the card.
  const onCardKeyDown = (e: React.KeyboardEvent<HTMLDialogElement>): void => {
    if (e.key !== "Tab" || !cardEl) return;
    const focusables = cardEl.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[120]" aria-hidden={false}>
      {/* Dimmer — full screen when centered; otherwise the spotlight's own
          box-shadow provides the dim so the anchored element stays bright. */}
      {step.target && rect ? (
        <div
          aria-hidden
          className="pointer-events-none absolute rounded-xl ring-2 ring-ando-orange transition-all duration-300"
          style={{
            top: rect.top - SPOT_PAD,
            left: rect.left - SPOT_PAD,
            width: rect.width + SPOT_PAD * 2,
            height: rect.height + SPOT_PAD * 2,
            boxShadow: "0 0 0 9999px rgba(17,17,17,0.55)",
          }}
        />
      ) : (
        <div aria-hidden className="absolute inset-0 bg-ink-1/55" />
      )}

      {/* Click-blocker so the page underneath isn't interactive mid-tour. */}
      <div aria-hidden className="absolute inset-0" />

      <AnimatePresence mode="wait">
        <motion.dialog
          key={step.id}
          open
          ref={setCardEl}
          aria-modal="true"
          aria-labelledby="demo-tour-title"
          aria-describedby="demo-tour-body"
          onKeyDown={onCardKeyDown}
          initial={reducedMotion ? false : { opacity: 0, y: 8, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={reducedMotion ? { opacity: 0 } : { opacity: 0, y: 6, scale: 0.98 }}
          transition={{ duration: reducedMotion ? 0 : 0.22, ease }}
          className={cn(
            "absolute m-0 flex flex-col gap-3 rounded-2xl border border-ando-orange/20 bg-card p-5",
            "shadow-[0_24px_60px_-20px_rgba(17,17,17,0.35)]",
          )}
          style={{ top: pos.top, left: pos.left, width: CARD_WIDTH }}
        >
          <div className="flex items-start justify-between gap-3">
            <span className="font-mono text-[11px] text-ink-3 uppercase tracking-[0.14em]">
              {stepIndex + 1} / {total}
            </span>
            <button
              type="button"
              onClick={endTour}
              aria-label={i18n._(msg`Salta il tour`)}
              className="-m-1 rounded-md p-1 text-ink-3 transition-colors hover:bg-ando-ink/[0.06] hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
            >
              <X size={15} strokeWidth={1.75} aria-hidden />
            </button>
          </div>

          <h2 id="demo-tour-title" className="font-semibold text-[16px] text-ink-1 leading-snug">
            {i18n._(step.title)}
          </h2>
          <p id="demo-tour-body" className="text-[13.5px] text-ink-2 leading-relaxed">
            {i18n._(mode === "user" && step.bodyUser ? step.bodyUser : step.body)}
          </p>

          {/* Progress dots */}
          <div className="mt-1 flex items-center gap-1.5" aria-hidden>
            {DEMO_TOUR_STEPS.map((s, i) => (
              <span
                key={s.id}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === stepIndex ? "w-5 bg-ando-orange" : "w-1.5 bg-ando-ink/15",
                )}
              />
            ))}
          </div>

          <div className="mt-1 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={endTour}
              className="rounded-lg px-2 py-1.5 text-[12.5px] text-ink-3 transition-colors hover:text-ink-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
            >
              {i18n._(msg`Salta`)}
            </button>
            <div className="flex items-center gap-2">
              {!isFirst ? (
                <button
                  type="button"
                  onClick={goPrev}
                  className="inline-flex items-center gap-1 rounded-lg border border-hairline px-3 py-1.5 text-[12.5px] text-ink-1 transition-colors hover:bg-ando-ink/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
                >
                  <ArrowLeft size={13} strokeWidth={1.75} aria-hidden />
                  {i18n._(msg`Indietro`)}
                </button>
              ) : null}
              <button
                data-tour-primary
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-1.5 rounded-lg bg-ando-orange px-3.5 py-1.5 font-semibold text-[12.5px] text-white transition-all hover:bg-ando-orange/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45"
              >
                {isLast ? i18n._(msg`Inizia`) : i18n._(msg`Avanti`)}
                {!isLast ? <ArrowRight size={13} strokeWidth={1.75} aria-hidden /> : null}
              </button>
            </div>
          </div>
        </motion.dialog>
      </AnimatePresence>
    </div>,
    document.body,
  );
}

DemoTour.displayName = "DemoTour";
